import React from 'react';
import { Button, Chip, Container } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { useTranslation } from 'react-i18next';
import { extractApiErrorMessage, notifications } from '../../utils';
import { Loader, ClearFilters, LocationStableSelect, AnimalTypeSelect } from '../../elements';
import { UpdateLocationModal } from '../../components';
import useAuthContext from '../../hooks/useAuthContext';
import useFeatures, { FeatureType } from '../../hooks/features';
import { LocationsService } from '../../hooks/LocationsService';
import { LocationStableMap } from '../../elements/LocationStableSelect';
import { LoaderSize } from '../../constants/constants';
import {
  LocationsHierarchyResult,
  LocationStableGroup,
  LocationRoomGroup,
  LocationViewModel,
  LocationPosition,
  LocationStable
} from '../../models/locations';
import RoomSchema from './RoomSchema';

interface RoomSelection {
  stable: LocationStable;
  room: string | null;
}

const sumAnimals = (locations: LocationViewModel[]): number =>
  locations.reduce((acc, loc) => acc + (loc.animalsCount ?? 0), 0);

const sumStableAnimals = (group: LocationStableGroup): number =>
  group.rooms.reduce((acc, room) => acc + sumAnimals(room.locations), 0);

const LocationsHierarchy: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const { hasFeature } = useFeatures();
  const locationsService = React.useMemo(() => new LocationsService(), []);

  const hasWelfareFeature = hasFeature(FeatureType.USE_WELFARE_AREA_CHECKING);

  const [stable, setStable] = React.useState<LocationStable>(LocationStable.Unknown);
  const [animalType, setAnimalType] = React.useState(0);

  const [hierarchy, setHierarchy] = React.useState<LocationsHierarchyResult>({ stables: [] });
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [expanded, setExpanded] = React.useState<Set<LocationStable>>(new Set());
  const [selection, setSelection] = React.useState<RoomSelection | null>(null);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [modalLocation, setModalLocation] = React.useState<LocationViewModel>({} as LocationViewModel);
  const [modalMode, setModalMode] = React.useState<'room' | 'pen'>('room');

  const loadHierarchy = React.useCallback(() => {
    setIsBusy(true);
    locationsService
      .getLocationsHierarchy(stable, animalType)
      .then((result) => {
        const stables = result?.stables ?? [];
        setHierarchy({ stables });
        setExpanded(new Set(stables.map((s) => s.stable)));
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  }, [locationsService, stable, animalType, t]);

  React.useEffect(() => {
    if (!loaded) {
      loadHierarchy();
    }
  }, [loaded, loadHierarchy]);

  const reload = () => setLoaded(false);

  const clearFilters = () => {
    setStable(LocationStable.Unknown);
    setAnimalType(0);
    setLoaded(false);
  };

  const toggleStable = (value: LocationStable) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }
      return next;
    });
  };

  const selectedRoomGroup: LocationRoomGroup | null = React.useMemo(() => {
    if (!selection) return null;
    const stableGroup = hierarchy.stables.find((s) => s.stable === selection.stable);
    return stableGroup?.rooms.find((r) => r.room === selection.room) ?? null;
  }, [selection, hierarchy]);

  const openAddRoom = (stableValue: LocationStable) => {
    setModalMode('room');
    setModalLocation({ farmId: state.farmId, stable: stableValue } as LocationViewModel);
    setDialogOpen(true);
  };

  const openAddPen = () => {
    if (!selection) return;
    setModalMode('pen');
    setModalLocation({
      farmId: state.farmId,
      stable: selection.stable,
      room: selection.room ?? ''
    } as LocationViewModel);
    setDialogOpen(true);
  };

  const onLocationUpdated = (location: LocationViewModel | null) => {
    setDialogOpen(false);
    if (!location) return;

    const onSuccess = () => {
      notifications.success(location.id ? t('f-loc-upd-ok') : t('f-loc-create-ok'));
      reload();
    };
    const onError = (e: unknown) => notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));

    if (location.id) {
      locationsService.updateLocation(location).then(onSuccess).catch(onError);
    } else {
      locationsService
        .createLocation(location)
        .then((result) => {
          if (result.success.length) {
            onSuccess();
          } else {
            notifications.error(t('f-loc-create-error'));
          }
        })
        .catch(onError);
    }
  };

  const onMovePen = (location: LocationViewModel, newPosition: LocationPosition) => {
    locationsService
      .updateLocation({ ...location, positioning: newPosition })
      .then(() => {
        notifications.success(t('f-loc-hier-move-ok'));
        reload();
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-loc-hier-move-error') }));
        reload();
      });
  };

  const isRoomSelected = (group: LocationStableGroup, room: LocationRoomGroup) =>
    selection?.stable === group.stable && selection?.room === room.room;

  return (
    <Container maxWidth={false} sx={{ maxWidth: 1280 }}>
      {/* filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="min-w-50">
          <LocationStableSelect
            value={stable}
            fullWidth
            allowAny
            onLocationStableChange={(v) => {
              setStable(v);
              setLoaded(false);
            }}
          />
        </div>
        <div className="min-w-50">
          <AnimalTypeSelect
            value={animalType}
            fullWidth
            allowAny
            onAnimalTypeChange={(v) => {
              setAnimalType(v);
              setLoaded(false);
            }}
          />
        </div>
        <ClearFilters onClick={clearFilters} />
      </div>

      <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

      {loaded && !isBusy && (
        <>
          {hierarchy.stables.length === 0 ? (
            <div className="text-center text-gray-500 py-16 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              {t('f-loc-hier-empty')}
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
              {/* master: stable -> room tree */}
              <div className="lg:col-span-1 flex flex-col gap-2">
                {hierarchy.stables.map((group) => {
                  const isExpanded = expanded.has(group.stable);
                  return (
                    <div key={group.stable} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                        <button
                          type="button"
                          className="flex items-center gap-2 text-left flex-1 appearance-none bg-transparent border-0 p-0 cursor-pointer font-[inherit]"
                          onClick={() => toggleStable(group.stable)}
                        >
                          {isExpanded ? (
                            <ExpandMoreIcon fontSize="small" className="text-gray-500" />
                          ) : (
                            <ChevronRightIcon fontSize="small" className="text-gray-500" />
                          )}
                          <span className="font-semibold text-gray-800 text-sm">
                            {t(LocationStableMap[group.stable]?.trans ?? 'f-loc-stable-unknown')}
                          </span>
                          <Chip
                            size="small"
                            variant="outlined"
                            label={t('f-loc-hier-rooms-count', { count: group.rooms.length })}
                          />
                          <Chip size="small" label={sumStableAnimals(group)} sx={{ bgcolor: 'grey.300' }} />
                        </button>
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<AddCircleIcon />}
                          onClick={() => openAddRoom(group.stable)}
                        >
                          {t('f-loc-hier-add-room')}
                        </Button>
                      </div>

                      {isExpanded && (
                        <ul className="list-none m-0 p-0 divide-y divide-gray-100">
                          {group.rooms.map((room, idx) => (
                            <li key={`${room.room ?? 'null'}-${idx}`}>
                              <button
                                type="button"
                                className={`w-full text-left px-4 py-2.5 flex items-center justify-between gap-2 appearance-none border-0 cursor-pointer font-[inherit] transition-colors ${
                                  isRoomSelected(group, room)
                                    ? 'bg-blue-50 border-l-4 border-blue-500'
                                    : 'bg-transparent hover:bg-gray-50'
                                }`}
                                onClick={() => setSelection({ stable: group.stable, room: room.room })}
                              >
                                <span className="flex items-center gap-2 text-sm text-gray-700">
                                  <MeetingRoomIcon fontSize="small" className="text-gray-400" />
                                  {room.room || t('f-loc-hier-unassigned')}
                                </span>
                                <span className="flex items-center gap-1 text-xs text-gray-500">
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    label={t('f-loc-hier-pens-count', { count: room.locations.length })}
                                  />
                                  <Chip size="small" label={sumAnimals(room.locations)} className="!bg-gray-100" />
                                </span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* detail: 2D room schema */}
              <div className="lg:col-span-2">
                {selectedRoomGroup ? (
                  <RoomSchema
                    room={selectedRoomGroup}
                    hasWelfareFeature={hasWelfareFeature}
                    onPenClick={(id) => navigate(`/locations/${id}`)}
                    onMovePen={onMovePen}
                    onAddPen={openAddPen}
                  />
                ) : (
                  <div className="min-h-[300px] flex items-center justify-center text-gray-500 bg-white rounded-lg shadow-sm border border-dashed border-gray-200">
                    {t('f-loc-hier-select-room')}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      <UpdateLocationModal
        location={modalLocation}
        opened={dialogOpen}
        showLastLocationMessage={false}
        lockRoom={modalMode === 'pen'}
        lockStable={modalMode === 'pen'}
        title={modalMode === 'pen' ? t('f-loc-hier-add-pen') : t('f-loc-hier-add-room')}
        infoText={
          modalMode === 'pen'
            ? t('f-loc-hier-add-pen-info', { room: selection?.room || t('f-loc-hier-unassigned') })
            : t('f-loc-hier-add-room-info')
        }
        onLocationUpdated={onLocationUpdated}
      />
    </Container>
  );
};

export default LocationsHierarchy;
